import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatCheck2Frontend } from "../actions/Checks/util";
import { formatMeta2Frontend } from "../actions/FacilityLocations/utils";
import { format2Frontend } from "../actions/Layers/util";
import { convert_to_comma_seperated_list } from "../actions/util";
import { Alert } from "../types/alerts";
import { toUrlDate } from "../utils/date";
import { callAvosApi } from "../utils/useAvosApiHook";

// Types for the API responses
export interface FacilityLocation {
  location_id: string;
  name: string;
  gateway?: string;
  controller_room?: string;
  [key: string]: any;
}

export interface AllocatedLayer {
  layers: any[];
  days: any[];
  [key: string]: any;
}

export interface Check {
  [key: string]: any;
}

export interface RegisterData {
  [key: string]: any;
}
export interface RegisterOption {
  key: string;
  label: string;
  min_value?: number | null;
  max_value?: number | null;
}

export interface RoomEvent {
  [key: string]: any;
}

export interface CheckSession {
  session_id: string;
  user_id: number;
  created: string;
  updated: string;
  tenant_id: string;
  location_id?: string;
  is_invalid: boolean;
  meta: any[];
  [key: string]: any;
}

export interface LocationPrediction {
  layer_id: string;
  location_id: string;
  ripening_date: string;
  ripening_day?: number | null;
  output: Record<string, any>;
}

// Hook to get a single facility location
export const useFacilityLocation = (locationId: string | undefined, onAlert?: (alert: Omit<Alert, "id" | "timestamp">) => void) => {
    return useQuery({
        queryKey: ["facilityLocation", locationId],
        queryFn: async (): Promise<FacilityLocation> => {
            try {
                const { data } = await callAvosApi(`/facility-locations/${locationId}`);
                return formatMeta2Frontend(data);
            } catch (error: any) {
                // Handle 403 status for facility location access
                if (error?.response?.status === 403 && onAlert) {
                    onAlert({
                        color: "danger",
                        message: "You do not have access to this facility location. Please contact support.",
                        source: "facilityLocation"
                    });
                }
                throw error;
            }
        },
        enabled: !!locationId,
    });
};

// Hook to list all facility locations
export const useFacilityLocations = (filter: { offset: number; limit: number; type?: string; stage?: string, groupBy?: string[], with_meta?: boolean }) => {
    const params = new URLSearchParams();
    params.append("offset", filter.offset.toString());
    params.append("limit", filter.limit.toString());
    if (filter.type) params.append("type", filter.type);
    if (filter.stage) params.append("stage", filter.stage);
    if (filter.groupBy) params.append("layer_group_by_attributes", filter.groupBy.join(","));
    if (filter.with_meta) params.append("with_meta", "true");

    return useQuery({
        queryKey: ["facilityLocations", filter],
        queryFn: async (): Promise<FacilityLocation[]> => {
            const { data } = await callAvosApi(`/facility-locations/?${params.toString()}`);
            return data.map(formatMeta2Frontend);
        },
    });
};

// Hook to get allocated layers for a location and date range
export const useAllocatedLayers = (
    locationId: string | undefined,
    dateFrom: string,
    dateTo: string
) => {
    return useQuery({
        queryKey: ["allocatedLayers", locationId, dateFrom, dateTo],
        queryFn: async (): Promise<AllocatedLayer> => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/allocated_layers/${dateFrom}/${dateTo}`);
            return {
                ...data,
                layers: data.layers.map(format2Frontend),
                days: data.days // days data doesn't need formatting
            };
        },
        enabled: !!locationId && !!dateFrom && !!dateTo,
        placeholderData: keepPreviousData,
    });
};

// Hook to get allocated layers for several ripening cycles at once (e.g. all cycles
// visible in the history modal), reusing the same allocated_layers endpoint per cycle's
// own date range. `enabled` should gate this on the modal actually being open.
// Cycle dates can be datetimes (e.g. "2026-10-19T13:20:43.146286"); they are sent as plain dates.
export const useAllocatedLayersForCycles = (
    locationId: string | undefined,
    cycles: { id: string; ripening_start_date?: string; ripening_end_date?: string }[],
    enabled: boolean
): Record<string, any[]> => {
    const queries = useQueries({
        queries: (cycles || []).map((cycle) => {
            const dateFrom = toUrlDate(cycle.ripening_start_date);
            const dateTo = toUrlDate(cycle.ripening_end_date);
            return {
                queryKey: ["allocatedLayers", locationId, dateFrom, dateTo],
                queryFn: async (): Promise<AllocatedLayer> => {
                    const { data } = await callAvosApi(`/facility-locations/${locationId}/allocated_layers/${dateFrom}/${dateTo}`);
                    return {
                        ...data,
                        layers: data.layers.map(format2Frontend),
                        days: data.days,
                    };
                },
                enabled: enabled && !!locationId && !!dateFrom && !!dateTo,
                placeholderData: keepPreviousData,
            };
        }),
    });

    const layersByCycleId: Record<string, any[]> = {};
    (cycles || []).forEach((cycle, index) => {
        layersByCycleId[cycle.id] = queries[index]?.data?.layers ?? [];
    });
    return layersByCycleId;
};

// Hook to get location checks for a location and date range
export const useLocationChecks = (
    locationId: string | undefined,
    dateFrom: string,
    dateTo: string
) => {
    return useQuery({
        queryKey: ["locationChecks", locationId, dateFrom, dateTo],
        queryFn: async (): Promise<Check[]> => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/checks/${dateFrom}/${dateTo}`);
            return data.map(formatCheck2Frontend);
        },
        enabled: !!locationId && !!dateFrom && !!dateTo,
        placeholderData: keepPreviousData,
    });
};

// Hook to get register data for a location and date range
export const useRegisterData = (
    locationId: string | undefined,
    dateFrom: string,
    dateTo: string,
    keys: string[] = []
) => {
    const params = new URLSearchParams();
    if (keys.length > 0) {
        params.append("keys", keys.join(","));
    }
    const querySuffix = params.toString() ? `?${params.toString()}` : "";
    return useQuery({
        queryKey: ["registerData", locationId, dateFrom, dateTo, keys.join(",")],
        queryFn: async (): Promise<RegisterData[]> => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/register-data/${dateFrom}/${dateTo}${querySuffix}`);
            return data;
        },
        enabled: !!locationId && !!dateFrom && !!dateTo,
        placeholderData: keepPreviousData,
    });
};

// Hook to get register data series for plotting charts
export const useRegisterDataSeries = (
    locationId: string | undefined,
    dateFrom: string,
    dateTo: string,
    keys: string[] = [],
    // * The cycle start: the graph begins there, not at midnight of its first day.
    start?: string | null
) => {
    const params = new URLSearchParams();
    if (keys.length > 0) {
        params.append("keys", keys.join(","));
    }
    if (start) {
        params.append("start", start);
    }
    const querySuffix = params.toString() ? `?${params.toString()}` : "";
    return useQuery({
        queryKey: ["registerDataSeries", locationId, dateFrom, dateTo, keys.join(","), start ?? null],
        queryFn: async () => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/register-data/${dateFrom}/${dateTo}/series${querySuffix}`);
            return data;
        },
        enabled: !!locationId && !!dateFrom && !!dateTo,
        placeholderData: keepPreviousData,
    });
};

export const useAvailableRegisterKeys = () => {
    return useQuery({
        queryKey: ["availableRegisterKeys"],
        queryFn: async (): Promise<RegisterOption[]> => {
            const { data } = await callAvosApi("/facility-locations/register-keys");
            return data || [];
        },
    });
};

// Hook to get prediction data for a location and date range
export const useLocationPredictions = (
    locationId: string | undefined,
    dateFrom: string,
    dateTo: string
) => {
    return useQuery({
        queryKey: ["locationPredictions", locationId, dateFrom, dateTo],
        queryFn: async (): Promise<LocationPrediction[]> => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/predictions/${dateFrom}/${dateTo}`);
            return data;
        },
        enabled: !!locationId && !!dateFrom && !!dateTo,
        placeholderData: keepPreviousData,
    });
};

// Hook to get today's register data for a location
export const useTodayRegisterData = (locationId: string | undefined) => {
    return useQuery({
        queryKey: ["todayRegisterData", locationId],
        queryFn: async (): Promise<RegisterData | null> => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/register-data/today`);
            return data;
        },
        enabled: !!locationId,
        refetchInterval: 10000, // Refetch every 10 seconds
    });
};

// Hook to get fruit sensor data (RipeTap + color sensor registers) for a location
export const useFruitSensorData = (
    locationId: string | undefined,
    dateFrom: string | undefined,
    dateTo: string | undefined,
    registerKeys?: string[]
) => {
    const keysParam = registerKeys?.length ? `?keys=${registerKeys.join(",")}` : "";
    return useQuery({
        queryKey: ["fruitSensorData", locationId, dateFrom, dateTo, registerKeys],
        queryFn: async (): Promise<any> => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/fruit-sensor-data/${dateFrom}/${dateTo}${keysParam}`);
            return data;
        },
        enabled: !!locationId && !!dateFrom && !!dateTo,
        placeholderData: keepPreviousData,
    });
};

// Hook to poll room events
export const useRoomEvents = (
    deviceUid: string | undefined,
    dateFrom: string | null = null,
    onAlert?: (alert: Omit<Alert, "id" | "timestamp">) => void
) => {
    const params = new URLSearchParams();
    if (dateFrom) params.append("date_from", dateFrom);

    const queryString = params.toString();
    const url = `/device/events/user/poll/${deviceUid}/room-events/${queryString ? `?${queryString}` : ""}`;

    return useQuery({
        queryKey: ["roomEvents", deviceUid, dateFrom],
        queryFn: async (): Promise<RoomEvent[]> => {
            try {
                const { data } = await callAvosApi(url, { no_toast: true });
                return data;
            } catch (error: any) {
                // Handle 403 status specifically for room events
                if (error?.response?.status === 403 && onAlert) {
                    onAlert({
                        color: "warning",
                        message: "This room gateway is not assigned to your tenant. Please contact support.",
                        source: "roomEvents"
                    });
                }
                throw error;
            }
        },
        enabled: !!deviceUid,
        refetchInterval: 10000, // Refetch every 10 seconds
        placeholderData: keepPreviousData,
    });
};

// Hook to get check sessions for a location and date range
export const useLocationCheckSessions = (
    locationId: string | undefined,
    dateFrom: string,
    dateTo: string
) => {
    return useQuery({
        queryKey: ["locationCheckSessions", locationId, dateFrom, dateTo],
        queryFn: async (): Promise<CheckSession[]> => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/check-sessions/${dateFrom}/${dateTo}`);
            return data.map(formatMeta2Frontend);
        },
        enabled: !!locationId && !!dateFrom && !!dateTo,
        placeholderData: keepPreviousData,
    });
};

// Hook to patch facility location with optimistic updates
export const usePatchFacilityLocation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ locationId, field, value, keepHistory = false, callApi = true }: {
            locationId: string;
            field: string;
            value: any;
            keepHistory?: boolean;
            callApi?: boolean;
        }) => {
            // If callApi is false, just return the data without making an API call
            if (!callApi) {
                return { locationId, field, value, response: null };
            }

            const data = {
                field,
                value: convert_to_comma_seperated_list(value),
                keep_history: keepHistory
            };

            const { data: response } = await callAvosApi(`/facility-locations/${locationId}`, {
                method: "PATCH",
                data
            });
            return { locationId, field, value, response };
        },
        onMutate: async ({ locationId, field, value }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({ queryKey: ["facilityLocation", locationId] });
            await queryClient.cancelQueries({ queryKey: ["facilityLocations"] });

            // Snapshot the previous value
            const previousLocation = queryClient.getQueryData(["facilityLocation", locationId]);
            const previousLocations = queryClient.getQueryData(["facilityLocations"]);

            // Optimistically update the single location
            if (previousLocation) {
                queryClient.setQueryData(["facilityLocation", locationId], (old: any) => ({
                    ...old,
                    [field]: value
                }));
            }

            // Optimistically update the locations list
            if (previousLocations) {
                queryClient.setQueryData(["facilityLocations"], (old: any) => ({
                    ...old,
                    results: old.results.map((location: any) => (location.location_id === locationId
                        ? { ...location, [field]: value }
                        : location))
                }));
            }

            // Return a context object with the snapshotted value
            return { previousLocation, previousLocations };
        },
        onError: (err, variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context?.previousLocation) {
                queryClient.setQueryData(["facilityLocation", variables.locationId], context.previousLocation);
            }
            if (context?.previousLocations) {
                queryClient.setQueryData(["facilityLocations"], context.previousLocations);
            }
        },
        onSettled: (data, error, variables) => {
            // Only refetch if we actually made an API call
            if (variables.callApi !== false) {
                queryClient.invalidateQueries({ queryKey: ["facilityLocation", variables.locationId] });
                queryClient.invalidateQueries({ queryKey: ["facilityLocations"] });
            }
        },
    });
};

type StartRipeningCyclePayload = {
    ripening_start_date: string;
    ripening_end_date: string;
    ripening_target?: string | null;
    season?: string | null;
    layer_ids: string[];
    meta?: any;
    ripening_program_id?: string;
    ripening_program_fruit_type?: string;
    idempotency_key?: string;
    // Required for gateway-backed rooms; verified server-side against the location.
    tenant_id?: string;
};

type StartRipeningCycleVariables = {
    locationId: string;
    payload: StartRipeningCyclePayload;
};

export type RipeningCycleStartStatus = "pending" | "accepted" | "rejected" | "timeout";

// * Result of starting a cycle (or its program again): the room's start is followed by its
// * command status; `command_message` explains a failed start in plain language.
export type RipeningCycleStartResult = {
    cycle_id: string;
    command_id?: string;
    command_status?: RipeningCycleStartStatus;
    command_message?: string;
    // * Set when the cycle started but the external system (SAP) rejected the cycle start.
    warning?: string;
};

// Hook to start ripening program for a location
export const useStartRipeningCycle = () => {
    return useMutation({
        mutationFn: async ({ locationId, payload }: StartRipeningCycleVariables): Promise<RipeningCycleStartResult> => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/start-ripening-cycle`, {
                method: "POST",
                data: payload
            });
            return data;
        },
    });
};

type RipeningCycleStartVariables = { locationId: string; cycleId: string };

// Hook to store (and read) the current status of a cycle's program start. Silent: the
// caller decides what to show.
export const useRefreshRipeningCycleStart = () => {
    return useMutation({
        mutationFn: async ({ locationId, cycleId }: RipeningCycleStartVariables): Promise<RipeningCycleStartResult> => {
            const { data } = await callAvosApi(
                `/facility-locations/${locationId}/ripening-cycle/${cycleId}/controller-command/refresh`,
                { method: "POST", no_toast: true },
            );
            return data;
        },
    });
};

// Hook to send the program of a cycle whose start failed to the ripening room again
export const useRetryRipeningCycleStart = () => {
    return useMutation({
        mutationFn: async ({ locationId, cycleId }: RipeningCycleStartVariables): Promise<RipeningCycleStartResult> => {
            const { data } = await callAvosApi(
                `/facility-locations/${locationId}/ripening-cycle/${cycleId}/retry-start`,
                { method: "POST" },
            );
            return data;
        },
    });
};

type RipeningCycle = {
    id: string;
    location_id: string;
    ripening_start_date: string;
    ripening_end_date: string;
    ripening_target?: string | null;
    season?: string | null;
    meta?: any;
    layer_ids: string[];
    created?: string;
    active?: boolean;
};

type RipeningCycleResponse = {
    results: RipeningCycle[];
    total: number;
    limit: number;
    offset: number;
};

export const useRipeningCycles = (locationId: string | undefined, limit = 12, offset = 0) => {
    return useQuery({
        queryKey: ["ripeningCycles", locationId, limit, offset],
        queryFn: async (): Promise<RipeningCycleResponse> => {
            const params = new URLSearchParams({
                limit: String(limit),
                offset: String(offset),
            });
            const { data } = await callAvosApi(`/facility-locations/${locationId}/ripening-cycles?${params.toString()}`);
            return data;
        },
        enabled: !!locationId,
    });
};

type EndRipeningCyclePayload = {
    ripening_start_date?: string;
    ripening_end_date?: string;
    ripening_target?: string | null;
    season?: string | null;
    meta?: any;
    // * false only when cancelling a start the room refused; ending a cycle stops the room.
    stop_room?: boolean;
};

type EndRipeningCycleVariables = {
    locationId: string;
    cycleId: string;
    payload: EndRipeningCyclePayload;
};

// Hook to end ripening cycle for a location
export const useEndRipeningCycle = () => {
    return useMutation({
        mutationFn: async ({ locationId, cycleId, payload }: EndRipeningCycleVariables) => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/end-ripening-cycle/${cycleId}`, {
                method: "POST",
                data: payload
            });
            return data;
        },
    });
};

type PatchRipeningCyclePayload = {
    ripening_start_date?: string;
    ripening_end_date?: string;
    ripening_target?: string | null;
    season?: string | null;
    meta?: any;
};

type PatchRipeningCycleVariables = {
    locationId: string;
    cycleId: string;
    payload: PatchRipeningCyclePayload;
};

// Hook to patch/update ripening cycle for a location
export const usePatchRipeningCycle = () => {
    return useMutation({
        mutationFn: async ({ locationId, cycleId, payload }: PatchRipeningCycleVariables) => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/ripening-cycle/${cycleId}`, {
                method: "PATCH",
                data: payload
            });
            return data;
        },
    });
};

export type RipeningCycleProgramPhaseEdit = {
    phase: number;
    t_end?: number;
    duration_hours?: number;
    ethylene_setpoint?: number;
    ethylene_active?: boolean;
    // * Switch a phase that has not started on or off (a disabled phase such as extra gas).
    enabled?: boolean;
};

// * The store settings a storage program may change while the room stores.
export type RipeningCycleProgramStoreEdit = {
    setpoint?: number;
    air_offset?: number;
    ventilation?: number;
    ethylene_setpoint?: number;
    co2_setpoint?: number;
    o2_setpoint?: number;
    scrubber_active?: boolean;
    n2_active?: boolean;
};

// * The general settings a ripening program may change while the room ripens (whole minutes).
export type RipeningCycleProgramGeneralEdit = {
    ventilation_period?: number;
    ventilation_interval?: number;
};

export type RipeningCycleProgramEdit = {
    phases: RipeningCycleProgramPhaseEdit[];
    proceed_to_next_phase?: boolean;
    store?: RipeningCycleProgramStoreEdit;
    general?: RipeningCycleProgramGeneralEdit;
    note?: string;
};

export type RipeningCycleProgramEditResult = {
    cycle_id: string;
    command_id: string;
    command_status?: string;
    current_phase?: number;
    ripening_end_date?: string;
    // * Set when the program changed but the external system (SAP) rejected the new end date.
    warning?: string;
};

const PROGRAM_EDIT_POLL_MS = 2500;
const PROGRAM_EDIT_MAX_POLLS = 120;
const wait = (milliseconds: number) => new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
});

async function waitForProgramEdit(
    locationId: string,
    cycleId: string,
    result: RipeningCycleProgramEditResult,
    attempt = 0,
): Promise<RipeningCycleProgramEditResult> {
    if (result.command_status !== "pending") return result;
    if (attempt >= PROGRAM_EDIT_MAX_POLLS) {
        throw new Error("The ripening room has not confirmed the program change yet.");
    }
    await wait(PROGRAM_EDIT_POLL_MS);
    const response = await callAvosApi(
        `/facility-locations/${locationId}/ripening-cycle/${cycleId}/program-command/refresh`,
        { method: "POST" },
    );
    return waitForProgramEdit(locationId, cycleId, response.data, attempt + 1);
}

// Hook to change setpoints/time of the program a running cycle's ripening room follows
export const useEditRipeningCycleProgram = () => {
    return useMutation({
        mutationFn: async ({ locationId, cycleId, payload }: { locationId: string; cycleId: string; payload: RipeningCycleProgramEdit }): Promise<RipeningCycleProgramEditResult> => {
            const { data } = await callAvosApi(`/facility-locations/${locationId}/ripening-cycle/${cycleId}/program`, {
                method: "POST",
                data: payload
            });
            return waitForProgramEdit(locationId, cycleId, data);
        },
    });
};

// Resume a pending edit after the page was reloaded or revisited.
export const useResumeRipeningCycleProgramEdit = () => {
    return useMutation({
        mutationFn: async ({ locationId, cycleId, commandId }: { locationId: string; cycleId: string; commandId: string }): Promise<RipeningCycleProgramEditResult> => {
            return waitForProgramEdit(locationId, cycleId, {
                cycle_id: cycleId,
                command_id: commandId,
                command_status: "pending",
            });
        },
    });
};

// Hook to list facility locations for a specific tenant with pagination
export const useTenantFacilityLocations = (
    tenantId: string | undefined,
    filter: { offset: number; limit: number; stage?: string; type?: string; facility?: string }
) => {
    const params = new URLSearchParams();
    params.append("offset", filter.offset.toString());
    params.append("limit", filter.limit.toString());
    if (filter.stage) params.append("stage", filter.stage);
    if (filter.type) params.append("type", filter.type);
    if (filter.facility) params.append("facility", filter.facility);

    return useQuery({
        queryKey: ["tenantFacilityLocations", tenantId, filter],
        queryFn: async (): Promise<{ results: FacilityLocation[]; count: number }> => {
            const { data } = await callAvosApi(`/tenants/${tenantId}/facility-locations?${params.toString()}`);
            return { results: data.results.map(formatMeta2Frontend), count: data.count };
        },
        enabled: !!tenantId,
    });
};

// Hook to create a facility location for a tenant
export const useCreateFacilityLocation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ tenantId, data }: { tenantId: string; data: any }) => {
            const response = await callAvosApi(`/tenants/${tenantId}/facility-locations`, {
                method: "POST",
                data,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["tenantFacilityLocations"] });
            queryClient.invalidateQueries({ queryKey: ["facilityLocations"] });
        },
    });
};

// Hook to update a facility location for a tenant
export const useUpdateTenantFacilityLocation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ tenantId, data }: { tenantId: string; data: any }) => {
            const response = await callAvosApi(`/tenants/${tenantId}/facility-locations/${data.location_id}`, {
                method: "PUT",
                data,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["tenantFacilityLocations"] });
            queryClient.invalidateQueries({ queryKey: ["facilityLocations"] });
        },
    });
};

// Hook to delete a facility location for a tenant
export const useDeleteTenantFacilityLocation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ tenantId, locationId }: { tenantId: string; locationId: string }) => {
            const response = await callAvosApi(`/tenants/${tenantId}/facility-locations/${locationId}`, {
                method: "DELETE",
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["tenantFacilityLocations"] });
            queryClient.invalidateQueries({ queryKey: ["facilityLocations"] });
        },
    });
};

// Hook to delete all facility locations for a tenant
export const useDeleteAllTenantFacilityLocations = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ tenantId }: { tenantId: string }) => {
            const response = await callAvosApi(`/tenants/${tenantId}/facility-locations/all`, {
                method: "DELETE",
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["tenantFacilityLocations"] });
            queryClient.invalidateQueries({ queryKey: ["facilityLocations"] });
        },
    });
};

// Hook to import facility locations for a tenant
export const useImportTenantFacilityLocations = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ tenantId, data }: { tenantId: string; data: any }) => {
            const response = await callAvosApi(`/tenants/${tenantId}/facility-locations/import`, {
                method: "POST",
                data,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["tenantFacilityLocations"] });
            queryClient.invalidateQueries({ queryKey: ["facilityLocations"] });
        },
    });
};

// Hook to update facility location positions for a tenant
export const useUpdateTenantFacilityLocationPositions = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ tenantId, positions }: { tenantId: string; positions: any[] }) => {
            const response = await callAvosApi(`/tenants/${tenantId}/facility-locations/positions`, {
                method: "PATCH",
                data: positions,
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["tenantFacilityLocations"] });
            queryClient.invalidateQueries({ queryKey: ["facilityLocations"] });
        },
    });
};

// * One cell linked to a room of a ripening room gateway; gateway and room both null unlinks it.
export type RoomLink = {
    location_id: string;
    gateway: string | null;
    controller_room: string | null;
};

/** Save the room links of several ripening rooms at once (all or none). */
export const useUpdateRoomLinks = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ tenantId, links }: { tenantId: string; links: RoomLink[] }) => {
            const response = await callAvosApi(`/tenants/${tenantId}/facility-locations/room-links`, {
                method: "PUT",
                data: { links },
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["tenantFacilityLocations"] });
            queryClient.invalidateQueries({ queryKey: ["facilityLocations"] });
        },
    });
};
